import { analyticsApi } from '../store/api/analyticsApi';
import { store } from '../store/store';
import { tokenService } from './tokenService';

export type AnalyticsEventName =
  | 'Viewed member list'
  | 'Viewed member'
  | 'Viewed artifact'
  | 'Viewed settings'
  | 'Viewed page'
  | 'Selected member'
  | 'Selected document'
  | 'Deselected document'
  | 'Clicked generate artifact'
  | 'Edited artifact'
  | 'Saved artifact'
  | 'Canceled edit artifact'
  | 'Copied artifact'
  | 'Selected artifact version'
  | 'User Submitted Feedback'
  | 'Signed out'
  | 'Created artifact note'
  | 'Edited artifact note'
  | 'Deleted artifact note'
  | 'Clicked note reflection link';

type AnalyticsTrackOptions = {
  clientEventId?: string;
  dedupeKey?: string;
  queryParams?: Record<string, string>;
};

const pageLoadId = crypto.randomUUID();
const dedupeClientEventIds = new Map<string, string>();

const logError = (eventName: string, error: unknown) => {
  if (import.meta.env.DEV) {
    const message = error instanceof Error ? error.message : String(error);
    console.debug(`[analytics] ${eventName} failed: ${message}`);
  }
};

type AnalyticsAuthState = {
  impersonation?: {
    active?: boolean;
  } | null;
};

type AnalyticsStoreState = {
  auth?: AnalyticsAuthState;
};

export const isAnalyticsSuppressedForImpersonation = (
  state: AnalyticsStoreState | null | undefined,
  isImpersonating: boolean
): boolean => {
  return state?.auth?.impersonation?.active === true || isImpersonating;
};

const resolveClientEventId = (options: AnalyticsTrackOptions): string => {
  if (options.clientEventId) {
    return options.clientEventId;
  }

  if (options.dedupeKey) {
    const scopedKey = `${pageLoadId}:${options.dedupeKey}`;
    const existing = dedupeClientEventIds.get(scopedKey);
    if (existing) {
      return existing;
    }

    const generated = crypto.randomUUID();
    dedupeClientEventIds.set(scopedKey, generated);
    return generated;
  }

  return crypto.randomUUID();
};

export const analytics = {
  track(
    eventName: AnalyticsEventName,
    properties: Record<string, unknown>,
    options: AnalyticsTrackOptions = {}
  ): void {
    if (isAnalyticsSuppressedForImpersonation(store.getState(), tokenService.isImpersonating())) {
      if (import.meta.env.DEV) {
        console.debug(`[analytics] ${eventName} suppressed during impersonation`);
      }
      return;
    }

    const clientEventId = resolveClientEventId(options);
    const occurredAt = new Date().toISOString();

    const result = store.dispatch(
      analyticsApi.endpoints.trackAnalyticsEvent.initiate({
        event_name: eventName,
        client_event_id: clientEventId,
        occurred_at: occurredAt,
        properties,
        query_params: options.queryParams,
      })
    );

    result.unwrap().catch((error) => {
      logError(eventName, error);
    });
  },

  viewed(
    eventName: AnalyticsEventName,
    screen: string,
    route: string,
    properties: Record<string, unknown> = {},
    options: AnalyticsTrackOptions = {}
  ): void {
    // Screen views carry the page URL's query parameters, e.g. email UTMs (MEN-5426).
    analytics.track(
      eventName,
      { screen, route, ...properties },
      {
        ...options,
        queryParams: Object.fromEntries(new URLSearchParams(window.location.search)),
      }
    );
  },
};
